import * as React from "react";
import {
  createRouter,
  createRootRoute,
  createRoute,
  redirect,
  useNavigate,
  Outlet,
} from "@tanstack/react-router";
import { z } from "zod";

// Import components
import { RootLayout } from "./routes/__root";
import { useAuth } from "@/hooks/useAuth";
import { Spinner } from "@/components/ui/spinner";
import { LoginPage } from "./pages/auth/login/LoginPage";
import { SignupPage } from "./pages/auth/signup/SignupPage";
import { ForgotPasswordPage } from "./pages/auth/forgot-password/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/auth/reset-password/ResetPasswordPage";
import { ProfilePage } from "./pages/profile/ProfilePage";
import { ProtectedRoute } from "./components/auth";
import { DashboardLayout } from "./components/dashboard";
import { NotFoundPage, ServerErrorPage } from "./pages/error";
import { MapPage } from "./pages/dashboard/MapPage";
import { ConfigsPage } from "./pages/dashboard/ConfigsPage";
import { AnalyticsPage } from "./pages/dashboard/AnalyticsPage";
import { CostTrackingPage } from "./pages/dashboard/CostTrackingPage";
import { SettingsPage } from "./pages/dashboard/SettingsPage";
import { ReviewPage } from "./pages/dashboard/ReviewPage";
import { CollectionsPage } from "./pages/dashboard/CollectionsPage";
import { CollectionDetailPage } from "./pages/dashboard/CollectionDetailPage";
import { ImportPage } from "./pages/dashboard/ImportPage";
import { HowItWorksPage } from "./pages/dashboard/HowItWorksPage";
import { ScanRequestsPage } from "./pages/dashboard/ScanRequestsPage";
import { SharedCollectionPage } from "./pages/share/SharedCollectionPage";
import { CityReportPage } from "./pages/share/CityReportPage";
import { PhysicalMappingPage } from "./pages/dashboard/PhysicalMappingPage";
import { PhysicalMappingLocationPage } from "./pages/dashboard/PhysicalMappingLocationPage";
import { MappingHomePage } from "./pages/mapping/MappingHomePage";
import { MappingLocationPage } from "./pages/mapping/MappingLocationPage";
import { MappingHelpPage } from "./pages/mapping/MappingHelpPage";
import { PublicHowItWorksPage } from "./pages/PublicHowItWorksPage";

/**
 * Dashboard Layout Wrapper
 *
 * Wraps dashboard routes with ProtectedRoute and DashboardLayout.
 */
function DashboardLayoutWrapper() {
  return (
    <ProtectedRoute>
      <DashboardLayout>
        <Outlet />
      </DashboardLayout>
    </ProtectedRoute>
  );
}

function IndexRedirect() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (loading) return;
    navigate({ to: user ? "/dashboard" : "/login", replace: true });
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Spinner className="size-8" />
    </div>
  );
}

/**
 * Route Tree
 *
 * Define the application's route hierarchy using TanStack Router.
 */

// Root route with layout and error handlers
const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
  errorComponent: ServerErrorPage,
});

// Index — redirects to /login or /dashboard based on auth
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: IndexRedirect,
});

// Auth routes
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
});

// Protected routes
const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/profile",
  component: ProfilePage,
});

// Dashboard layout route (parent for all dashboard pages)
const dashboardLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  component: DashboardLayoutWrapper,
});

// Search param schema for dashboard map filters (persisted in URL)
// Using .optional().catch() so the input type allows partial objects (for navigate/redirect)
// while .catch() ensures invalid URL values safely fall back to undefined
const dashboardSearchSchema = z.object({
  classification: z.enum(["all", "sport_field", "no_field", "unclassified"]).optional().catch(undefined),
  ownership: z.enum(["all", "public", "private", "institutional", "unknown"]).optional().catch(undefined),
  reviewStatus: z.enum(["all", "unreviewed", "reviewed", "rejected"]).optional().catch(undefined),
  source: z.enum(["all", "equip_scout", "city", "external_import"]).optional().catch(undefined),
  collectionIds: z.array(z.string()).optional().catch(undefined),
  enableClustering: z.boolean().optional().catch(undefined),
  sportTypes: z.array(z.string()).optional().catch(undefined),
  locationTypes: z.array(z.string()).optional().catch(undefined),
  showCourtPins: z.boolean().optional().catch(undefined),
  location: z.string().optional().catch(undefined),
});

// Dashboard index route (map-first home)
const dashboardIndexRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/",
  component: MapPage,
  validateSearch: (search) => dashboardSearchSchema.parse(search),
});

// Dashboard sub-routes
const configsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/configs",
  component: ConfigsPage,
});

// Redirect old location routes to map page
const locationsRedirectRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/locations",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard", search: {} });
  },
});

const locationDetailRedirectRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/locations/$locationId",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/dashboard",
      search: { location: params.locationId },
    });
  },
});

const analyticsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/analytics",
  component: AnalyticsPage,
});

const costsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/costs",
  component: CostTrackingPage,
});

const reviewRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/review",
  component: ReviewPage,
});

const collectionsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/collections",
  component: CollectionsPage,
});

const collectionDetailRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/collections/$collectionId",
  component: CollectionDetailPage,
});

const importRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/import",
  component: ImportPage,
});

const howItWorksRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/how-it-works",
  component: HowItWorksPage,
});

const scanRequestsSearchSchema = z.object({
  new: z.boolean().optional().catch(undefined),
});

const scanRequestsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/scan-requests",
  component: ScanRequestsPage,
  validateSearch: (search) => scanRequestsSearchSchema.parse(search),
});

const settingsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/settings",
  component: SettingsPage,
});

// Public share route (no auth required)
const shareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/share/$token",
  component: SharedCollectionPage,
});

// Public city-review report route (no auth required)
const cityReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/city-review/$token",
  component: CityReportPage,
});

// Public "How it works" route (no auth required) — investor-facing mirror
const publicHowItWorksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/how-it-works",
  component: PublicHowItWorksPage,
});

// Physical mapping admin routes
const physicalMappingSearchSchema = z.object({
  collectionId: z.string().optional().catch(undefined),
  tab: z.enum(["overview", "review", "city-review"]).optional().catch(undefined),
});

const physicalMappingRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/physical-mapping",
  component: PhysicalMappingPage,
  validateSearch: (search) => physicalMappingSearchSchema.parse(search),
});

const physicalMappingLocationRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/physical-mapping/$locationId",
  component: PhysicalMappingLocationPage,
  validateSearch: (search) => physicalMappingSearchSchema.parse(search),
});

// Public mapper routes (no auth required) — mobile-first
const mappingHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mapping/$token",
  component: MappingHomePage,
});

const mappingLocationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mapping/$token/location/$locationId",
  component: MappingLocationPage,
});

const mappingHelpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mapping/$token/help",
  component: MappingHelpPage,
});

// Build the route tree
const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  profileRoute,
  shareRoute,
  cityReviewRoute,
  publicHowItWorksRoute,
  mappingHomeRoute,
  mappingLocationRoute,
  mappingHelpRoute,
  dashboardLayoutRoute.addChildren([
    dashboardIndexRoute,
    configsRoute,
    locationsRedirectRoute,
    locationDetailRedirectRoute,
    collectionsRoute,
    collectionDetailRoute,
    importRoute,
    reviewRoute,
    analyticsRoute,
    costsRoute,
    howItWorksRoute,
    scanRequestsRoute,
    settingsRoute,
    physicalMappingRoute,
    physicalMappingLocationRoute,
  ]),
]);

/**
 * Router Instance
 *
 * The main router for the application.
 * Export this to use in App.tsx with RouterProvider.
 */
export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultNotFoundComponent: NotFoundPage,
  defaultErrorComponent: ServerErrorPage,
});

// Type declaration for TypeScript
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export default router;
